import React, { useState, useContext, useMemo, useEffect } from "react";
import AppContext from "../../../context/AppContext";
import FileChooserPreview from "../../../components/common/FileChooserPreview";
import translations from "../../../components/common/Translations";
import {
  matchesVisibilityRules,
  nestedFieldVisibleByRadioOrRules,
} from "../../../utils/workflowVisibilityRules";
import {
  COMPARE_CONDITION_NOT_EQUAL,
  evaluateCompareValidation,
  flattenWorkflowFields,
} from "../../../utils/workflowCompareValidation";
import {
  getLinkedWorkflowIdForOption,
  hydrateLinkedWorkflowRules,
  isLinkableFieldType,
  applyLinkedWorkflowMarkers,
  splitLinkedWorkflowMarkerFields,
  stripLinkedWorkflowMarkerFields,
} from "../../../utils/workflowLinkedWorkflowRules";
import { fetchWorkflowData } from "../../../service/workflow";

/** Flat API payloads list dependents as siblings with visibilityRules (no radioInputValues tree). */
function hasActiveVisibilityRules(field) {
  const rules = field.visibilityRules;
  if (!Array.isArray(rules) || rules?.length === 0) return false;
  return rules.some(
    (r) => Array.isArray(r.conditions) && r.conditions?.length > 0
  );
}

function isFlatPreviewFieldVisible(field, formData) {
  if (!hasActiveVisibilityRules(field)) return true;
  return matchesVisibilityRules(field.visibilityRules, formData);
}

function hasVisibilityConditionForOption(fields, sourceUniqueKey, optionValue) {
  return flattenWorkflowFields(fields).some(
    (field) =>
      Array.isArray(field?.visibilityRules) &&
      field.visibilityRules.some(
        (rule) =>
          Array.isArray(rule?.conditions) &&
          rule.conditions.some(
            (condition) =>
              condition?.sourceUniqueKey === sourceUniqueKey &&
              condition?.expectedValue === optionValue,
          ),
      ),
  );
}

/**
 * A linked workflow can itself link further workflows. Nothing stops that chain
 * from looping back (A links B, B links A), so the preview stops expanding after
 * a few hops rather than recursing until the stack gives out.
 */
const MAX_LINKED_WORKFLOW_DEPTH = 3;

/** Which of a field's options the preview currently has selected. */
function selectedOptionValues(field, formData) {
  const value = formData[field.uniqueKey];
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || String(value) === "") return [];
  return [value];
}

const PreviewForm = ({
  fields,
  currentPage,
  isPageWise,
  linkedWorkflowOptions = [],
}) => {
  const [formData, setFormData] = useState({});
  const [selectedRadioIds, setSelectedRadioIds] = useState({});
  const { theme, langMode } = useContext(AppContext);

  const workflowNameById = useMemo(
    () =>
      linkedWorkflowOptions.reduce((acc, option) => {
        acc[option.value] = option.label;
        return acc;
      }, {}),
    [linkedWorkflowOptions]
  );

  // Metadata of the workflows an option links to, keyed by workflow id and
  // fetched only once an option pointing at one is actually selected.
  const [linkedWorkflows, setLinkedWorkflows] = useState({});

  const linkedWorkflowName = (linkedId) =>
    linkedWorkflows[linkedId]?.name ??
    workflowNameById[linkedId] ??
    `Workflow ${linkedId}`;

  const linkedWorkflowLabel = (field, optionValue) => {
    const linkedId = getLinkedWorkflowIdForOption(field, optionValue);
    if (linkedId == null) return null;
    return linkedWorkflowName(linkedId);
  };

  const renderLinkedWorkflowBadge = (field, optionValue) => {
    const label = linkedWorkflowLabel(field, optionValue);
    if (!label) return null;
    return (
      <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-[#236DB4] dark:bg-blue-900/40 dark:text-blue-200">
        {translations[langMode]?.linkedWorkflowLinkedTo ?? "Linked workflow"}:{" "}
        {label}
      </span>
    );
  };

  // Every linked workflow the current answers reach, including links that live
  // inside an already-loaded linked workflow, so a chain resolves one hop per
  // render as each fetch lands.
  const activeLinkedWorkflowIds = useMemo(() => {
    const seen = new Set();

    const scan = (items, depth) => {
      if (depth >= MAX_LINKED_WORKFLOW_DEPTH) return;
      flattenWorkflowFields(items).forEach((field) => {
        if (!isLinkableFieldType(field.fieldType)) return;
        selectedOptionValues(field, formData).forEach((optionValue) => {
          const linkedId = getLinkedWorkflowIdForOption(field, optionValue);
          if (linkedId == null || seen.has(linkedId)) return;
          seen.add(linkedId);
          const loaded = linkedWorkflows[linkedId];
          if (loaded?.fields) scan(loaded.fields, depth + 1);
        });
      });
    };

    scan(fields, 0);
    return [...seen];
  }, [fields, formData, linkedWorkflows]);

  // Joined so the effect keys off the ids themselves rather than the array's
  // identity, which changes on every render.
  const activeLinkedWorkflowKey = activeLinkedWorkflowIds.join(",");

  useEffect(() => {
    const missing = activeLinkedWorkflowIds.filter((id) => !linkedWorkflows[id]);
    if (missing.length === 0) return;

    setLinkedWorkflows((prev) => {
      const next = { ...prev };
      missing.forEach((id) => {
        next[id] = { status: "loading" };
      });
      return next;
    });

    missing.forEach(async (id) => {
      try {
        const data = await fetchWorkflowData(id);
        const { markerFields, normalFields } =
          splitLinkedWorkflowMarkerFields(data.workflowFields ?? []);
        const dragItems = stripLinkedWorkflowMarkerFields(
          data.existDragItems ?? []
        );
        // dragItems is the nested tree the renderer below expects; fieldsMetadata
        // is the flat fallback (dependents as siblings carrying visibilityRules).
        const linkedFields = dragItems?.length
          ? hydrateLinkedWorkflowRules(dragItems, normalFields)
          : (normalFields ?? []);
        const seededFields = applyLinkedWorkflowMarkers(
          linkedFields,
          markerFields
        );
        setLinkedWorkflows((prev) => ({
          ...prev,
          [id]: {
            status: "loaded",
            name: data.existWorkflowName,
            fields: seededFields,
          },
        }));
      } catch (err) {
        console.error(`Error fetching linked workflow ${id}:`, err);
        setLinkedWorkflows((prev) => ({
          ...prev,
          [id]: { status: "error", message: err?.message },
        }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLinkedWorkflowKey]);

  const themeCtrl = `${theme === "dark"
      ? "dark-mode "
      : theme === "high-contrast"
        ? "high-contrast"
        : ""
    }`;

  const handleRadioChange = (parentKey, inputKey, inputValue) => {
    setSelectedRadioIds((prev) => ({ ...prev, [parentKey]: inputKey }));
    setFormData((prev) => ({ ...prev, [parentKey]: inputValue }));
  };

  // A file input's `.value` is the browser's fake path (e.g. "C:\fakepath\photo.jpg") --
  // this only affects what's displayed, not what's stored via handleInputChange.
  const getFileDisplayName = (value) => {
    if (!value) return translations[langMode]?.noFile ?? "No file chosen";
    return String(value).split(/[\\/]/).pop();
  };

  const handleInputChange = (key, value, fieldType) => {
    if (fieldType === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [key]: Array.isArray(prev[key])
          ? prev[key].includes(value)
            ? prev[key].filter((val) => val !== value)
            : [...prev[key], value]
          : [value],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  // Field-to-field comparisons configured in the builder are evaluated live here
  // so the admin can see the rule pass/fail without leaving the workflow maker.
  const allPreviewFields = useMemo(
    () => flattenWorkflowFields(fields),
    [fields]
  );

  // A rule is configured on the primary field but reported on the field it
  // points at: that's the one filled in second (e.g. "Confirm Meter Number"),
  // so the message lands under the input where the mismatch was just typed
  // rather than above it on a field the person already left.
  const rulesBySecondaryKey = useMemo(() => {
    const map = new Map();
    allPreviewFields.forEach((primaryField) => {
      const rules = Array.isArray(primaryField.compareValidations)
        ? primaryField.compareValidations
        : [];
      rules.forEach((rule) => {
        if (!rule?.secondaryUniqueKey) return;
        const existing = map.get(rule.secondaryUniqueKey) || [];
        existing.push({
          ...rule,
          primaryUniqueKey: primaryField.uniqueKey,
          primaryFieldName: primaryField.fieldName,
        });
        map.set(rule.secondaryUniqueKey, existing);
      });
    });
    return map;
  }, [allPreviewFields]);

  const defaultCompareError = (rule) => {
    const key =
      rule.condition === COMPARE_CONDITION_NOT_EQUAL
        ? "compareValidationDefaultNotEqualError"
        : "compareValidationDefaultEqualError";
    const template =
      translations[langMode]?.[key] ?? translations.en[key] ?? "";
    return template.replace("{field}", rule.primaryFieldName ?? "");
  };

  // Both sides have to be filled in before a comparison says anything useful,
  // so a half-typed pair isn't reported as a failure.
  const compareErrorsFor = (field) => {
    const rules = rulesBySecondaryKey.get(field.uniqueKey) || [];
    if (rules.length === 0) return [];

    const value = formData[field.uniqueKey];
    if (value === undefined || String(value).trim() === "") return [];

    return rules.reduce((messages, rule) => {
      const primaryValue = formData[rule.primaryUniqueKey];
      if (primaryValue === undefined || String(primaryValue).trim() === "") {
        return messages;
      }
      if (evaluateCompareValidation(rule.condition, primaryValue, value)) {
        return messages;
      }
      messages.push(rule.errorMessage || defaultCompareError(rule));
      return messages;
    }, []);
  };

  /**
   * The linked workflow's own form, expanded under the option that selects it.
   * `renderField` is referenced before its declaration below, which is fine --
   * this only runs while rendering, by which point it's initialised.
   */
  const renderLinkedWorkflowFields = (field, optionValue, linkDepth) => {
    const linkedId = getLinkedWorkflowIdForOption(field, optionValue);
    if (linkedId == null) return null;
    if (!selectedOptionValues(field, formData).includes(optionValue)) {
      return null;
    }

    const name = linkedWorkflowName(linkedId);
    // The option is named in the header because the block no longer sits under
    // the row that selected it -- and a checkbox can expand several at once.
    const shell = (inner) => (
      <div className="my-2 rounded-md border border-blue-200 bg-blue-50/40 p-3 dark:border-blue-700 dark:bg-blue-900/20">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-[#236DB4] dark:text-blue-200">
          {translations[langMode]?.linkedWorkflowLinkedTo ?? "Linked workflow"} (
          {optionValue}): {name}
        </p>
        {inner}
      </div>
    );

    // `linkDepth` is the nesting level of the form this option sits in, so the
    // workflow about to be expanded is level linkDepth + 1. Same boundary the
    // fetch scan above uses, so nothing is fetched that won't be rendered.
    if (linkDepth >= MAX_LINKED_WORKFLOW_DEPTH) {
      return shell(
        <p className="text-[11px] text-slate-500">
          {translations[langMode]?.linkedWorkflowDepthLimit ??
            "Nested linked workflows aren't expanded any further in the preview."}
        </p>
      );
    }

    const entry = linkedWorkflows[linkedId];

    if (!entry || entry.status === "loading") {
      return shell(
        <p className="text-[11px] text-slate-500">
          {translations[langMode]?.linkedWorkflowLoading ??
            "Loading workflows..."}
        </p>
      );
    }

    if (entry.status === "error") {
      return shell(
        <p className="text-[11px] text-red-500">
          {translations[langMode]?.linkedWorkflowLoadFailed ??
            "Couldn't load this linked workflow."}
        </p>
      );
    }

    // The linked workflow is a form of its own, so every page of it is shown at
    // once rather than following the outer preview's page cursor.
    const linkedFields = (entry.fields ?? []).filter((linkedField) =>
      isFlatPreviewFieldVisible(linkedField, formData)
    );

    if (linkedFields.length === 0) {
      return shell(
        <p className="text-[11px] text-slate-500">
          {translations[langMode]?.linkedWorkflowNoFields ??
            "This workflow has no fields to preview."}
        </p>
      );
    }

    return shell(
      <div className="flex flex-col gap-2">
        {linkedFields.map((linkedField) =>
          renderField(linkedField, linkDepth + 1)
        )}
      </div>
    );
  };

  /**
   * The linked workflows for whichever of the field's options are selected,
   * rendered once below the field rather than inline under an option row -- the
   * linked form is a whole questionnaire, so threading it between the options
   * breaks up the list it belongs to. A checkbox can have several options
   * checked, so this can expand more than one.
   */
  const renderLinkedWorkflowsForField = (field, linkDepth) => {
    const selected = selectedOptionValues(field, formData).filter(
      (optionValue) =>
        getLinkedWorkflowIdForOption(field, optionValue) != null &&
        !hasVisibilityConditionForOption(
          fields,
          field.uniqueKey,
          optionValue,
        )
    );
    if (selected.length === 0) return null;

    return selected.map((optionValue) => (
      <React.Fragment key={`linked-${field.uniqueKey}-${optionValue}`}>
        {renderLinkedWorkflowFields(field, optionValue, linkDepth)}
      </React.Fragment>
    ));
  };

  const renderField = (field, linkDepth = 0) => {
    const compareErrors = compareErrorsFor(field);
    const wrap = (inner) => (
      <div key={field.uniqueKey} className="flex flex-col gap-2">
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium">
              {/* A linked workflow's fields are numbered within their own form;
                  omit the prefix rather than print "undefined." if it's absent. */}
              {field.queNo != null ? `${field.queNo}. ` : ""}
              {field.fieldName}
              {field.isMandatory && (
                <span className="text-red-500 ml-1 text-[16px]" aria-hidden="true">
                  *
                </span>
              )}
            </label>
            <label className="text-sm uppercase text-gray-500">
              Type: {field.fieldType}
            </label>
          </div>
          {inner}
          {compareErrors.map((message) => (
            <p key={message} className="-mt-3 mb-2 text-xs text-red-500">
              {message}
            </p>
          ))}
        </div>
      </div>
    );

    switch (field.fieldType) {
      case "radio": {
        const controls = (
          <>
            {Array.isArray(field.allInputValues) &&
              field.allInputValues.map((input) => (
                <div key={input.inputKey} className="my-3">
                  <input
                    type="radio"
                    id={input.inputKey}
                    name={field.uniqueKey}
                    value={input.inputValue}
                    checked={formData[field.uniqueKey] === input.inputValue}
                    onChange={() =>
                      handleRadioChange(
                        field.uniqueKey,
                        input.inputKey,
                        input.inputValue
                      )
                    }
                    className={`mr-2 h-5 w-5 text-sm ${themeCtrl}`}
                  />
                  <label htmlFor={input.inputKey} className="text-sm">
                    {input.inputValue}{" "}
                  </label>
                  {renderLinkedWorkflowBadge(field, input.inputValue)}
                </div>
              ))}
            {field.radioInputValues &&
              field.radioInputValues
                .filter((nestedField) =>
                  nestedFieldVisibleByRadioOrRules(
                    nestedField,
                    formData,
                    field.uniqueKey,
                    selectedRadioIds[field.uniqueKey]
                  )
                )
                .map((nestedField) => (
                  <div
                    key={nestedField.uniqueKey}
                    className="py-2 pl-3 ml-2 border-l-2 border-blue-200 dark:border-blue-700"
                  >
                    {renderField(nestedField, linkDepth)}
                  </div>
                ))}
            {renderLinkedWorkflowsForField(field, linkDepth)}
          </>
        );
        return wrap(controls);
      }

      case "select": {
        const selectedOption =
          Array.isArray(field.allInputValues) &&
          field.allInputValues.find(
            (option) => option.inputValue === formData[field.uniqueKey]
          );
        const selectedOptionKey = selectedOption
          ? selectedOption.inputKey
          : null;
        const controls = (
          <>
            <select
              className={`border p-2 my-2 text-sm rounded-md w-full ${themeCtrl}`}
              value={formData[field.uniqueKey] || ""}
              onChange={(e) =>
                handleInputChange(
                  field.uniqueKey,
                  e.target.value,
                  field.fieldType
                )
              }
            >
              {Array.isArray(field.allInputValues) &&
                field.allInputValues.map((input) => {
                  // An <option> can only hold text, so the link is appended to
                  // the label rather than rendered as the badge used elsewhere.
                  // `value` stays the bare option so formData is unaffected.
                  const linked = linkedWorkflowLabel(field, input.inputValue);
                  return (
                    <option
                      key={input.inputKey}
                      value={input.inputValue}
                      className="text-sm"
                    >
                      {linked
                        ? `${input.inputValue} (${
                            translations[langMode]?.linkedWorkflowLinkedTo ??
                            "Linked workflow"
                          }: ${linked})`
                        : input.inputValue}
                    </option>
                  );
                })}
            </select>
            {selectedOptionKey &&
              field.radioInputValues &&
              field.radioInputValues
                .filter((nestedField) =>
                  nestedFieldVisibleByRadioOrRules(
                    nestedField,
                    formData,
                    field.uniqueKey,
                    selectedOptionKey
                  )
                )
                .map((nestedField) => (
                  <div
                    key={nestedField.uniqueKey}
                    className="py-2 pl-3 ml-2 border-l-2 border-blue-200 dark:border-blue-700"
                  >
                    {renderField(nestedField, linkDepth)}
                  </div>
                ))}
            {renderLinkedWorkflowsForField(field, linkDepth)}
          </>
        );
        return wrap(controls);
      }

      case "checkbox": {
        const controls = (
          <div>
            {Array.isArray(field.allInputValues) &&
              field.allInputValues.map((input) => {
                const isChecked =
                  Array.isArray(formData[field.uniqueKey]) &&
                  formData[field.uniqueKey].includes(input.inputValue);
                return (
                  <React.Fragment key={input.inputKey}>
                    <div className="flex items-center mb-2 text-sm">
                      <input
                        type="checkbox"
                        className={`mr-2 h-5 w-5 text-sm ${themeCtrl}`}
                        id={input.inputKey}
                        name={field.uniqueKey}
                        value={input.inputValue}
                        checked={!!isChecked}
                        onChange={(e) =>
                          handleInputChange(
                            field.uniqueKey,
                            e.target.value,
                            field.fieldType
                          )
                        }
                      />
                      <label htmlFor={input.inputKey} className="text-sm">
                        {input.inputValue}
                      </label>
                      {renderLinkedWorkflowBadge(field, input.inputValue)}
                    </div>
                    {field.radioInputValues &&
                      isChecked &&
                      field.radioInputValues
                        .filter((nestedField) =>
                          nestedFieldVisibleByRadioOrRules(
                            nestedField,
                            formData,
                            field.uniqueKey,
                            input.inputKey
                          )
                        )
                        .map((nestedField) => (
                          <div
                            key={nestedField.uniqueKey}
                            className="py-2 pl-3 ml-4 border-l-2 border-blue-200 dark:border-blue-700"
                          >
                            {renderField(nestedField, linkDepth)}
                          </div>
                        ))}
                  </React.Fragment>
                );
              })}
            {renderLinkedWorkflowsForField(field, linkDepth)}
          </div>
        );
        return wrap(controls);
      }

      case "textarea":
        return wrap(
          <textarea
            className={`border p-2 rounded-md w-full my-5 text-sm min-h-[80px] ${themeCtrl}`}
            placeholder={field.fieldName}
            value={formData[field.uniqueKey] || ""}
            onChange={(e) =>
              handleInputChange(
                field.uniqueKey,
                e.target.value,
                field.fieldType
              )
            }
          />
        );

      case "text":
      case "email":
      case "number":
      case "date":
      case "time":
      case "scan":
        return wrap(
          <input
            type={field.fieldType === "scan" ? "text" : field.fieldType}
            className={`border p-2 rounded-md w-full my-5 text-sm ${themeCtrl}`}
            placeholder={field.fieldName}
            value={formData[field.uniqueKey] || ""}
            onChange={(e) =>
              handleInputChange(
                field.uniqueKey,
                e.target.value,
                field.fieldType
              )
            }
          />
        );

      case "file":
      case "multi_image":
        return wrap(
          <FileChooserPreview
            id={field.uniqueKey}
            multiple={field.fieldType === "multi_image"}
            onChange={(e) =>
              handleInputChange(
                field.uniqueKey,
                e.target.value,
                field.fieldType
              )
            }
            theme={theme}
            size="md"
            chooseFileText={translations[langMode]?.chooseFile ?? "Choose File"}
            fileName={getFileDisplayName(formData[field.uniqueKey])}
          />
        );

      case "gps":
      case "ocr":
      case "signal_quality":
        return wrap(
          <input
            type="text"
            className={`border p-2 rounded-md w-full my-5 text-sm ${themeCtrl}`}
            placeholder={field.fieldName}
            value={formData[field.uniqueKey] || ""}
            onChange={(e) =>
              handleInputChange(
                field.uniqueKey,
                e.target.value,
                field.fieldType
              )
            }
          />
        );

      default:
        return wrap(
          <p className="text-xs text-gray-500">
            Preview for type &quot;{field.fieldType}&quot; is not available.
          </p>
        );
    }
  };

  const pageFields = Array.isArray(fields)
    ? fields.filter(
        (f) =>
          (!isPageWise || f.page === currentPage) &&
          isFlatPreviewFieldVisible(f, formData)
      )
    : [];
  const emptyPreviewMessage =
    langMode === "ar"
      ? "لا توجد حقول في هذه الصفحة للمعاينة."
      : langMode === "ur"
        ? "اس صفحے پر پیش نظارہ کے لیے کوئی فیلڈ موجود نہیں ہے۔"
        : "No fields available to preview on this page.";

  return (
    <div className="p-5">
      {pageFields.length > 0 ? (
        pageFields.map((field) => renderField(field))
      ) : (
        <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-4 text-center">
          <p className="text-sm font-medium text-slate-500">{emptyPreviewMessage}</p>
        </div>
      )}
    </div>
  );
};

export default PreviewForm;
